import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import BrandIcon from '@/components/BrandIcon';
import { site, products, channels, pricingPlans } from '@/data/content';

export default function Product() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'ProductGroup',
      name: 'ZuriHub Products',
      description: site.description,
      brand: { '@type': 'Brand', name: site.name },
      hasVariant: products.map((p) => ({
        '@type': 'Product',
        name: p.name,
        description: p.description,
      })),
    },
  ];

  const productIcons: Record<string, string> = {
    'zyra-ai': 'Sparkles',
    'zuri-cloud': 'Cloud',
    'zuri-voice': 'Phone',
  };

  const [freePlan, proPlan] = pricingPlans.slice(0, 2);

  return (
    <>
      <SEO
        title="Product"
        description="Meet Zyra AI, Zuri Cloud, and Zuri Voice. The products that power ZuriHub."
        path="/product"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-24 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/50 px-4 py-1.5 text-sm font-semibold text-slate-300 backdrop-blur">
            <Icon name="Zap" className="h-4 w-4 text-brand-400" /> One platform. Three products.
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-6xl">
            The complete <span className="gradient-text">CX stack</span> for modern teams
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-300">
            ZuriHub unifies AI, cloud, and voice into one workspace. Serve customers across every channel without switching tools.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Start free trial <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/features"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Explore features <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-400">
            <Icon name="Star" className="h-4 w-4 text-yellow-400" /> 4.8/5 from 250+ teams
          </p>
        </div>
      </section>

      <section className="py-24" aria-labelledby="products-list">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="products-list" className="section-title">Meet the platform</h2>
            <p className="section-subtitle mx-auto">Three products. One workspace. No switching tools.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {products.map((product, index) => (
              <div
                key={product.id}
                className={`group relative overflow-hidden rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl ${
                  index === 1 ? 'md:scale-[1.02] ring-1 ring-brand-200' : ''
                }`}
              >
                {index === 1 && (
                  <span className="absolute right-6 top-6 rounded-full bg-brand-100 px-3 py-1 text-xs font-bold text-brand-700">
                    Core Platform
                  </span>
                )}
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner transition group-hover:from-brand-600 group-hover:to-brand-700 group-hover:text-white">
                  <Icon name={productIcons[product.id]} className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">{product.name}</h3>
                <p className="mt-2 font-semibold text-brand-600">{product.tagline}</p>
                <p className="mt-4 text-slate-600">{product.description}</p>
                <ul className="mt-6 space-y-3">
                  {product.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
                >
                  Try {product.name} <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="how-heading" className="section-title">How it works</h2>
            <p className="section-subtitle mx-auto">Set up in minutes. Serve customers at scale.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              {
                step: '01',
                icon: 'Plug',
                title: 'Connect your channels',
                description:
                  'Link WhatsApp, email, chat, and social so every conversation lands in one inbox.',
              },
              {
                step: '02',
                icon: 'Bot',
                title: 'Train Zyra AI',
                description:
                  'Point Zyra at your docs and past tickets. It learns your tone, policies, and answers.',
              },
              {
                step: '03',
                icon: 'Users',
                title: 'Serve customers',
                description:
                  'Agents use smart replies, summaries, and omnichannel context to close tickets faster.',
              },
            ].map((s) => (
              <div
                key={s.step}
                className="relative rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
              >
                <span className="absolute right-6 top-6 text-5xl font-extrabold text-slate-100">
                  {s.step}
                </span>
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <Icon name={s.icon} className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="mt-3 text-slate-600">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="usecases-heading" className="section-title">Built for every team</h2>
            <p className="section-subtitle mx-auto">
              One platform that adapts to how your business serves customers.
            </p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: 'Headphones',
                title: 'Customer Support',
                description: 'Resolve tickets across every channel with AI and team collaboration.',
              },
              {
                icon: 'TrendingUp',
                title: 'Sales & Pre-Sales',
                description: 'Capture leads, answer questions, and convert visitors before they leave.',
              },
              {
                icon: 'UserPlus',
                title: 'Customer Onboarding',
                description:
                  'Guide new customers with knowledge bases, live chat, and proactive check-ins.',
              },
              {
                icon: 'Settings',
                title: 'Operations',
                description: 'Route, tag, invoice, and report so nothing gets lost behind the scenes.',
              },
            ].map((u) => (
              <div
                key={u.title}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <Icon name={u.icon} className="h-6 w-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{u.title}</h3>
                <p className="mt-3 text-sm text-slate-600">{u.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-950 py-24 text-white" aria-labelledby="channels-heading">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/20 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="channels-heading" className="section-title text-white">All your channels, one inbox</h2>
            <p className="section-subtitle mx-auto text-slate-300">
              Connect the channels your customers already use. Reply from a single, shared workspace.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {channels.map((c) => (
              <div
                key={c.name}
                className="group flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-700/50 bg-slate-800/40 p-6 text-center transition hover:-translate-y-1 hover:bg-slate-800/60 hover:shadow-xl"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-700/50 text-white transition group-hover:scale-110">
                  <BrandIcon name={c.brand} className="h-7 w-7" />
                </div>
                <span className="font-semibold text-slate-200">{c.name}</span>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-slate-400">
            Add channels in minutes. No switching apps. No missed messages.
          </p>
        </div>
      </section>

      <section className="py-24" aria-labelledby="pricing-heading">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 id="pricing-heading" className="section-title">Simple, transparent pricing</h2>
            <p className="section-subtitle mx-auto">Start free. Pay only when you need more.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
              <h3 className="text-2xl font-bold text-slate-900">{freePlan.name}</h3>
              <p className="mt-2 text-slate-600">{freePlan.description}</p>
              <p className="mt-4 text-5xl font-extrabold text-slate-900">{freePlan.price}</p>
              <p className="text-sm text-slate-500">/{freePlan.period}</p>
              <ul className="mt-6 space-y-3">
                {freePlan.features.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                    {b}
                  </li>
                ))}
              </ul>
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-900 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-800"
              >
                {freePlan.cta} <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
            <div className="relative rounded-3xl border border-brand-200 bg-brand-50 p-8 shadow-sm ring-1 ring-brand-200">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3 py-1 text-xs font-semibold text-white">
                Most Popular
              </span>
              <h3 className="text-2xl font-bold text-slate-900">{proPlan.name}</h3>
              <p className="mt-2 text-slate-600">{proPlan.description}</p>
              <p className="mt-4 text-5xl font-extrabold text-brand-700">{proPlan.price}</p>
              <p className="text-sm text-slate-600">/{proPlan.period}</p>
              <ul className="mt-6 space-y-3">
                {proPlan.features.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                    {b}
                  </li>
                ))}
              </ul>
              <a
                href={site.workspaceUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-brand-700"
              >
                {proPlan.cta} <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
          <p className="mt-8 text-center text-sm text-slate-500">
            Need Enterprise with voice, custom SLAs, and SSO? <Link to="/pricing" className="font-semibold text-brand-600 hover:underline">See full pricing</Link>.
          </p>
        </div>
      </section>

      <section className="bg-slate-900 py-24 text-white" aria-label="Start your free trial">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Ready to modernize your customer experience?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            Join teams that serve customers faster, cheaper, and happier on ZuriHub.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-brand-700 transition hover:bg-slate-100"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
          <p className="mt-4 text-sm text-slate-400">No credit card required. Free forever plan for 1 agent.</p>
        </div>
      </section>
    </>
  );
}
